import type { TFunction } from 'i18next';

import type { IGoogleAdsKeywordIdea } from '@feathr/blackbox';
import { EGoogleAdsKeywordMatchType } from '@feathr/blackbox';

import type { IAdGroupKeyword } from '../AdStep.types';
import { isKeywordTooGeneric } from '../AdStep.utils';

/**
 * The suggestions not already used by the ad group, and not single-word (Ad Grant policy
 * blocks single-word keywords, so a single-word idea is never worth offering).
 *
 * The endpoint deliberately returns suggestions the ad group already uses, so that
 * removing a keyword offers its suggestion again without another round trip. Hiding them
 * is this function's job, and it happens on every render rather than once on fetch.
 *
 * Matching ignores case and surrounding whitespace, since `food pantry` and `Food Pantry`
 * are the same bid to Google and would otherwise both be offered.
 *
 * @param ideas - Suggestions as returned by the endpoint.
 * @param keywords - The ad group's current positive keywords.
 * @returns The suggestions still worth offering, in the order Google ranked them.
 */
export function getAvailableIdeas(
  ideas: IGoogleAdsKeywordIdea[],
  keywords: IAdGroupKeyword[],
): IGoogleAdsKeywordIdea[] {
  const used = new Set(keywords.map((keyword) => keyword.text.trim().toLowerCase()));
  return ideas.filter(
    (idea) => !used.has(idea.text.trim().toLowerCase()) && !isKeywordTooGeneric(idea.text),
  );
}

/**
 * A digest of the keywords a suggestion request would be seeded with.
 *
 * Drives whether asking again is worthwhile: identical keywords produce an identical
 * digest, and would produce an identical list. Normalized the same way the suggestions
 * are matched — case and surrounding whitespace ignored, order irrelevant.
 *
 * @param keywords - The ad group's current positive keywords.
 * @returns A digest that changes only when the seeds meaningfully change.
 */
export function getSeedSignature(keywords: string[]): string {
  return [...keywords]
    .map((keyword) => keyword.trim().toLowerCase())
    .sort((a, b) => a.localeCompare(b))
    .join('|');
}

/**
 * The keyword texts a suggestion request should be seeded with.
 *
 * Taken from what the editor currently shows rather than what is stored: the wizard holds
 * keyword edits in memory until publish, so the stored list is routinely stale.
 *
 * @param keywords - The ad group's current positive keywords.
 * @returns The non-empty keyword texts, trimmed.
 */
export function getSeedKeywords(keywords: IAdGroupKeyword[]): string[] {
  return keywords.map((keyword) => keyword.text.trim()).filter(Boolean);
}

export interface IMatchTypeOption {
  id: EGoogleAdsKeywordMatchType;
  name: string;
}

/**
 * Human-readable label for a keyword match type. Kept out of the components so the editor's
 * pickers and the read-only summary can't drift apart.
 */
export function getMatchTypeLabel(matchType: EGoogleAdsKeywordMatchType, t: TFunction): string {
  const labels: Record<EGoogleAdsKeywordMatchType, string> = {
    [EGoogleAdsKeywordMatchType.BROAD]: t('Broad'),
    [EGoogleAdsKeywordMatchType.PHRASE]: t('Phrase'),
    [EGoogleAdsKeywordMatchType.EXACT]: t('Exact'),
  };
  return labels[matchType];
}

/**
 * Match type options for `UniversalSelect`. Its default option getters read `name`
 * then `id`, so no custom getters are needed.
 */
export function getMatchTypeOptions(t: TFunction): IMatchTypeOption[] {
  return Object.values(EGoogleAdsKeywordMatchType).map((matchType) => ({
    id: matchType,
    name: getMatchTypeLabel(matchType, t),
  }));
}
